import { useContext } from "react";
import { FormContextMA } from "../form-context";
import {
  capitalizeFirstLetter,
  formatToYYYY_MMM_DD,
} from "../../../helpers/functions";
import {
  classificationResultCalculation,
  GetPharmacologicalCards,
} from "../../patient/clinical-services/service-steps/treatment/data";
import {
  Section,
  Divider,
  H2,
  H3,
  P,
  CheckInput,
  TealSubSection,
  DataField,
} from "./common-components";
import { GetDropDownValue } from "./data";
import { reasonOptions } from "../../patient/clinical-services/service-steps/clinical-assessment/data";
import { GetAlertConfirmation } from "../../../helpers/constant";

export const TreatmentPlan = ({
  endService = null,
  clinicalServiceName,
  treatment,
  formType,
}) => {
  // const { clinicalServiceName, treatment } = useContext(FormContextMA);
  const minorAilmentName = { allergicRhinitis: "Allergic Rhinitis" }[
    clinicalServiceName
  ];

  const { frequencyOfSymptoms = null, levelOfSeverity = null } =
    treatment || {};

  const classification = classificationResultCalculation(
    frequencyOfSymptoms,
    levelOfSeverity
  );
  const firstLineTherapyPrescriptions = {
    label:
      classification === "Mild Intermittent"
        ? "First Line Therapy - Oral Antihistamine"
        : "First Line Therapy - Intranasal Corticosteroids",
    medication: treatment?.firstLineTherapyPrescriptions?.length
      ? [...treatment.firstLineTherapyPrescriptions]
      : [],
  };

  const stepUpTherapyPrescriptions = {
    label:
      classification === "Mild Intermittent"
        ? "Step Up Therapy - Intranasal Corticosteroids"
        : "Step Up Therapy - Intranasal Corticosteroids/ Antihistamines",
    medication: treatment?.stepUpTherapyPrescriptions?.length
      ? [...treatment.stepUpTherapyPrescriptions]
      : [],
  };
  const alternativeFirstLineTherapyPrescriptions = {
    label:
      "Alternative First Line Therapy - Intranasal Corticosteroids/ Antihistamines",
    medication: treatment?.alternativeFirstLineTherapyPrescriptions?.length
      ? [...treatment.alternativeFirstLineTherapyPrescriptions]
      : [],
  };
  const addOnTherapyOralDecongestantsPrescriptions = {
    label: "Oral Decongestants",
    medication: treatment?.addOnTherapyOralDecongestantsPrescriptions?.length
      ? [...treatment.addOnTherapyOralDecongestantsPrescriptions]
      : [],
  };
  const addOnTherapyIntranasalDecongestantsPrescriptions = {
    label: "Intranasal Decongestants",
    medication: treatment?.addOnTherapyIntranasalDecongestantsPrescriptions
      ?.length
      ? [...treatment.addOnTherapyIntranasalDecongestantsPrescriptions]
      : [],
  };
  const addOnTherapyOralAntihistaminesPrescriptions = {
    label: "Oral Antihistamines",
    medication: treatment?.addOnTherapyOralAntihistaminesPrescriptions?.length
      ? [...treatment.addOnTherapyOralAntihistaminesPrescriptions]
      : [],
  };
  const nasalSalineRinsePrescriptions = {
    label: "Nasal Saline Rinses",
    medication: treatment?.nasalSalineRinsePrescriptions?.length
      ? [...treatment.nasalSalineRinsePrescriptions]
      : [],
  };
  const nasalSalineSprayPrescriptions = {
    label: "Nasal Saline Sprays or Drops",
    medication: treatment?.nasalSalineSprayPrescriptions?.length
      ? [...treatment.nasalSalineSprayPrescriptions]
      : [],
  };
  const nasalLubricantPrescriptions = {
    label: "Nasal Lubricants & Gels",
    medication: treatment?.nasalLubricantPrescriptions?.length
      ? [...treatment.nasalLubricantPrescriptions]
      : [],
  };
  const isValidDate = (d) => {
    const part = (d || "").split("T")[0];
    return !!part && part !== "0001-01-01";
  };
  const getPharmacologicalDrug = (therapy) => {
    let drug = therapy.medication[0];
    return (
      <div className="space-y-2">
        <CheckInput label={therapy.label} checked={therapy.medication.length} />
        {therapy.medication.length > 0 && (
          <TealSubSection isBlue={false} className="space-y-1">
            <DataField value={capitalizeFirstLetter(drug?.medicationName)} />
            {drug?.sig && <DataField label={"SIG"} value={drug?.sig} />}
            <div className="grid grid-cols-4">
              <DataField label={"QTY"} value={drug?.dispenseQuantity} />
              <DataField label={"Refills"} value={drug?.refills ?? 0} />
            </div>
            <DataField
              label={"Reason"}
              value={capitalizeFirstLetter(
                GetDropDownValue(reasonOptions, drug?.reason)
              )}
            />
            <DataField
              label={"Special Instructions"}
              value={capitalizeFirstLetter(drug?.comments)}
            />
            {(() => {
              const hasDuration = !!(
                drug &&
                typeof drug.duration !== "undefined" &&
                drug.duration !== null &&
                String(drug.duration).trim() !== "" &&
                String(drug.duration).trim() !== "0"
              );

              // Partial grey separator (not full width)
              return (
                <>
                  <div className="w-3/4 border-t border-gray-200 my-2" />
                  <div className="grid grid-cols-2">
                    <DataField
                      label={"Duration:"}
                      value={hasDuration ? `${drug.duration}` : "N/A"}
                    />
                    <DataField
                      label={"Unit:"}
                      value={hasDuration ? (drug.unit || "") : "N/A"}
                    />
                  </div>
                  <div className="grid grid-cols-2">
                    <DataField
                      label={"Start Date:"}
                      value={hasDuration && isValidDate(drug?.startDate) ? formatToYYYY_MMM_DD(drug.startDate) : "N/A"}
                    />
                    <DataField
                      label={"End Date:"}
                      value={hasDuration && isValidDate(drug?.endDate) ? formatToYYYY_MMM_DD(drug.endDate) : "N/A"}
                    />
                  </div>
                </>
              );
            })()}
          </TealSubSection>
        )}
      </div>
    );
  };

  return (
    <Section formType={formType}>
      <H2>Goals for Therapy - {minorAilmentName}</H2>
      <div className="space-y-1">
        <ul className="list-disc space-y-[0.0416667in] text-[0.125in] ml-[0.25in]">
          <li>Provide Relief from signs and symptoms from allergies</li>
          <li>Prevent symptoms by avoiding allergen exposure</li>
          <li>Minimize adverse effects of treatment</li>
          <li>Improve quality of life</li>
        </ul>

        <TealSubSection isBlue={false}>
          <DataField
            label={"Personal Goals (Optional)"}
            value={treatment?.personalGoals}
          />
        </TealSubSection>
        {treatment?.howToAchieveGoals && (
          <TealSubSection isBlue={false}>
            <DataField
              label={"How to achieve the goals? (Optional)"}
              value={treatment?.howToAchieveGoals}
            />
          </TealSubSection>
        )}
        <Divider />
      </div>
      {/* <div className="space-y-[0.0833333in]">
        <div className="space-y-[0.0416667in]">
          <H3>Treatment for {minorAilmentName}</H3>
          <div className="flex flex-row gap-[0.0625in]">
            <P className="whitespace-nowrap">Prescriptions Issued:</P>
            <P>
              {[
                ...(treatment?.nasalSalineRinsePrescriptions || []),
                ...(treatment?.nasalSalineSprayPrescriptions || []),
                ...(treatment?.nasalLubricantPrescriptions || []),
                ...(treatment?.firstLineTherapyPrescriptions || []),
                ...(treatment?.stepUpTherapyPrescriptions || []),
                ...(treatment?.alternativeFirstLineTherapyPrescriptions || []),
                ...(treatment?.addOnTherapyPrescriptions || []),
              ]
                .map((p) => p.medicationName)
                .join(", ")}
            </P>
          </div>
        </div>
        <Divider />
      </div> */}
      {!endService?.endServiceObj.pharmacologicalHistoryEndService && (
        <>
          <div className="space-y-[0.0833333in]">
            <div className="space-y-[0.0416667in]">
              <H3>Prevention Treatment</H3>
              {/* TODO: text for avoidance tips? */}
              <DataField label={"Provided Allergen Avoidance Tips"} />
            </div>
            <Divider />
          </div>
          <div className="space-y-[0.0833333in]">
              <H3>Non-Pharmacological Treatment</H3>
              <div className="space-y-2">
                {nasalSalineRinsePrescriptions.medication?.length > 0 &&
                  getPharmacologicalDrug(nasalSalineRinsePrescriptions)}
              </div>
              <div className="space-y-2">
                {nasalSalineSprayPrescriptions.medication?.length > 0 &&
                  getPharmacologicalDrug(nasalSalineSprayPrescriptions)}
              </div>
              <div className="space-y-2">
                {nasalLubricantPrescriptions.medication?.length > 0 &&
                  getPharmacologicalDrug(nasalLubricantPrescriptions)}
              </div>
              <CheckInput
                label="None"
                checked={
                  nasalSalineRinsePrescriptions.medication?.length === 0 &&
                  nasalSalineSprayPrescriptions.medication?.length === 0 &&
                  nasalLubricantPrescriptions.medication?.length === 0
                }
              />
            <Divider />
          </div>
          <div className="space-y-[0.0833333in]">
            <H3>Pharmacological Treatment</H3>
            <TealSubSection className="space-y-2 !mt-2">
              <DataField value={`My Results: ${classification}`} />
            </TealSubSection>
            {firstLineTherapyPrescriptions.medication?.length > 0 &&
              getPharmacologicalDrug(firstLineTherapyPrescriptions)}
            {stepUpTherapyPrescriptions.medication?.length > 0 &&
              getPharmacologicalDrug(stepUpTherapyPrescriptions)}
            {alternativeFirstLineTherapyPrescriptions.medication?.length > 0 &&
              getPharmacologicalDrug(alternativeFirstLineTherapyPrescriptions)}
            <CheckInput
              label="No Prescription Added"
              checked={
                firstLineTherapyPrescriptions.medication?.length === 0 &&
                stepUpTherapyPrescriptions.medication?.length === 0 &&
                alternativeFirstLineTherapyPrescriptions.medication?.length ===
                  0
              }
            />
            <Divider />
          </div>
          <div className="space-y-4">
            <H3>Add On Therapy</H3>
            {getPharmacologicalDrug(addOnTherapyOralDecongestantsPrescriptions)}
            {getPharmacologicalDrug(
              addOnTherapyIntranasalDecongestantsPrescriptions
            )}
            {classification !== "Mild Intermittent" &&
              getPharmacologicalDrug(
                addOnTherapyOralAntihistaminesPrescriptions
              )}
            <CheckInput
              label="None"
              checked={
                addOnTherapyOralDecongestantsPrescriptions.medication
                  ?.length === 0 &&
                addOnTherapyIntranasalDecongestantsPrescriptions.medication
                  ?.length === 0 &&
                (classification === "Mild Intermittent" ||
                  addOnTherapyOralAntihistaminesPrescriptions.medication
                    ?.length === 0)
              }
            />
            {treatment?.pharmacologicalConfirmationType !== 0 && (
              <TealSubSection className="space-y-[0.0416667in]">
                {/* TODO: data for values? */}
                <DataField
                  label="Referral Reason"
                  value={"Pharmacological Treatment"}
                />
                <DataField
                  label="Referral Type"
                  value={GetAlertConfirmation(
                    treatment?.pharmacologicalConfirmationType
                  )}
                />
                {(treatment?.pharmacologicalConfirmationType === 1 ||
                  treatment?.pharmacologicalConfirmationType === 2) && (
                  <DataField
                    label="Explanation"
                    value={treatment?.pharmacologicalExplaination}
                  />
                )}
                <DataField
                  label={`Pharmacist Clinical Decision to ${
                    treatment?.pharmacologicalConfirmationType === 4
                      ? "End Service"
                      : "Continue with Service"
                  }`}
                  value={""}
                />
              </TealSubSection>
            )}
          </div>
        </>
      )}
    </Section>
  );
};
